import {
  type KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/chords/Button';
import { ModalDialog } from '~/chords/ModalDialog';
import { Icon } from '~/components/Icon';
import { cn } from '~/lib/cn';
import { FlexCol } from '~/modules/artist-roster/components/flex/FlexCol';
import { FlexRow } from '~/modules/artist-roster/components/flex/FlexRow';

export interface ChatPrompterProps {
  isProcessingQuestion: boolean;
  onStop: () => void;
  onAskQuestion: (question: string) => void;
  className?: string;
}

export function ChatPrompter({
  isProcessingQuestion,
  onStop,
  onAskQuestion,
  className,
}: ChatPrompterProps) {
  const { t } = useTranslation('data', { keyPrefix: 'musicIq.chatPrompter' });
  const [inputValue, setInputValue] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  // We need to know if the user stopped the question so we don't reset the input.
  const wasStoppedByUser = useRef(false);

  const askQuestion = useCallback(() => {
    const trimmed = inputValue.trim();

    if (trimmed && !isProcessingQuestion) {
      wasStoppedByUser.current = false;
      onAskQuestion(trimmed);
    }
  }, [inputValue, isProcessingQuestion, onAskQuestion]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      askQuestion();
    }
  };

  const handleButtonClick = useCallback(() => {
    if (isProcessingQuestion) {
      wasStoppedByUser.current = true;
      onStop();
    } else {
      askQuestion();
    }
  }, [isProcessingQuestion, onStop, askQuestion]);

  const hasInput = !!inputValue.trim().length;
  const isAskingDisabled = !hasInput && !isProcessingQuestion;

  useEffect(() => {
    // Don't reset the input if it's the user who aborted the question.
    if (wasStoppedByUser.current) {
      return;
    }

    // Clear the input once we've successfully processed the question (e.g. - we've
    // stopped receiving responses from the chatbot).
    if (!isProcessingQuestion) {
      setInputValue('');
    }
  }, [isProcessingQuestion]);

  return (
    <>
      <FlexCol
        id="chat-prompter"
        className={cn(
          'w-full gap-md rounded-sm border px-lg py-md bg-level-02 border-emphasis-subtle',
          className,
        )}
        role="form"
        aria-label={t('inputPlaceholder')}
      >
        <FlexRow className="items-center gap-sm">
          <div
            className={cn('grow rounded-sm', isProcessingQuestion && 'p-[1px]')}
            // Add border gradient
            style={
              isProcessingQuestion
                ? {
                    background:
                      'linear-gradient(to right, oklch(var(--bg-tertiary-default)), oklch(var(--bg-primary-default)))',
                  }
                : undefined
            }
          >
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('inputPlaceholder')}
              disabled={isProcessingQuestion}
              className={cn(
                'h-[42px] w-full text-body-sm text-default',
                'placeholder:text-subtle disabled:cursor-not-allowed',
                'rounded-sm p-sm outline-none bg-transparent',
                isProcessingQuestion
                  ? 'bg-level-02'
                  : 'border bg-emphasis-default border-emphasis-subtle',
              )}
              aria-label={t('inputPlaceholder')}
            />
          </div>

          <button
            type="button"
            onClick={handleButtonClick}
            disabled={isAskingDisabled}
            className={cn(
              'size-[42px] rounded-sm',
              'text-default',
              'disabled:cursor-not-allowed disabled:bg-emphasis-default disabled:text-disabled',
              {
                'border-2xs bg-emphasis-default border-emphasis-subtle':
                  isProcessingQuestion || hasInput,
              },
            )}
            aria-label={
              isProcessingQuestion ? t('stopAriaLabel') : t('askAriaLabel')
            }
          >
            {isProcessingQuestion ? (
              <Icon type="circle-stop" variant="solid" size="md" />
            ) : (
              <Icon type="arrow-up" variant="solid" size="md" />
            )}
          </button>
        </FlexRow>

        {/* Disclaimer */}
        <div className="flex gap-x-xs text-left text-caption-md font-semibold text-info-default">
          {t('disclaimer')}
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="font-semibold underline text-info-default hover:text-info-hover"
          >
            {t('learnMore')}
          </button>
        </div>
      </FlexCol>

      <ModalDialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <ModalDialog.Header>{t('modal.title')}</ModalDialog.Header>
        <ModalDialog.Body
          dangerouslyOverrideClassName={{
            __className: 'flex flex-col gap-md text-body-sm text-default',
          }}
        >
          <p>{t('modal.body')}</p>
          <p>{t('modal.bodySecondary')}</p>
        </ModalDialog.Body>
        <ModalDialog.Footer>
          <ModalDialog.Close>
            <Button variant="primary">{t('modal.ok')}</Button>
          </ModalDialog.Close>
        </ModalDialog.Footer>
      </ModalDialog>
    </>
  );
}

ChatPrompter.displayName = 'ChatPrompter';
